/**
 * ForgotPage — 找回密码
 * 视觉风格与登录页统一
 */
import { Typography, message } from 'antd';
import { Form, Input, Button } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import FireflyField from '../../components/FireflyField';

const { Text } = Typography;

export default function ForgotPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onFinish = async (values: { email: string }) => {
    setLoading(true);
    try {
      // TODO: 调用发送重置邮件 API
      setSubmitted(true);
      message.success('重置链接已发送到您的邮箱，请查收');
    } catch (err: any) {
      message.error(err?.message || '发送失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'relative',
      overflow: 'hidden',
      padding: '0',
    }}>
      {/* ═══ 背景 ═══ */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'url(/hero-deer-forest.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        zIndex: 0,
      }} />
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(90deg, rgba(4,15,34,0.38) 0%, rgba(4,15,34,0.22) 40%, rgba(4,15,34,0.08) 70%, transparent 100%)',
        zIndex: 1,
      }} />
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, transparent 60%, rgba(7,21,45,0.15) 80%, rgba(7,21,45,0.35) 100%)',
        zIndex: 1,
      }} />

      {/* 萤火虫 */}
      <FireflyField count={5} area="default" />

      {/* ═══ 面板 ═══ */}
      <div className="forgot-panel" style={{
        position: 'relative',
        zIndex: 10,
        width: 440,
        maxWidth: '90vw',
        marginLeft: '8vw',
        padding: '44px 40px',
        background: 'rgba(8, 22, 40, 0.62)',
        borderRadius: 24,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1px solid rgba(220,235,255,0.12)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logo-cat-white.png" alt="Simon Cat Agent" style={{ width: 40, height: 40, marginBottom: 16 }} />
          <div style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 20,
            fontWeight: 400,
            color: 'var(--text-primary)',
            marginBottom: 4,
          }}>
            找回密码
          </div>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            输入注册邮箱，我们将发送重置链接给您
          </Text>
        </div>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 20, fontSize: 14, lineHeight: 1.7 }}>
              重置密码链接已发送到您的邮箱，请登录邮箱查看
            </p>
            <Link
              to="/login"
              style={{
                color: '#E9D38A',
                fontSize: 14,
                fontFamily: 'var(--font-sans)',
              }}
            >
              <ArrowLeftOutlined style={{ marginRight: 6 }} />
              返回登录
            </Link>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="邮箱"
              name="email"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入正确的邮箱格式' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
                placeholder="请输入注册邮箱"
                style={{
                  height: 52,
                  background: 'rgba(255,255,255,0.045)',
                  border: '1px solid rgba(220,235,255,0.10)',
                  borderRadius: 14,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                }}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                style={{
                  height: 52,
                  background: 'linear-gradient(135deg, #F2D87E 0%, #DDB94F 100%)',
                  border: 'none',
                  borderRadius: 14,
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#08162A',
                  boxShadow: '0 4px 20px rgba(235,216,126,0.18)',
                }}
              >
                发送重置链接
              </Button>
            </Form.Item>
            <div style={{ textAlign: 'center' }}>
              <Link
                to="/login"
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                  transition: 'color 150ms',
                }}
              >
                <ArrowLeftOutlined style={{ marginRight: 6, fontSize: 11 }} />
                返回登录
              </Link>
            </div>
          </Form>
        )}

        <div style={{
          textAlign: 'center',
          marginTop: 28,
          color: 'rgba(220,235,255,0.30)',
          fontSize: 12,
          fontFamily: 'var(--font-sans)',
        }}>
          © 2026 西蒙的猫 · 萤火秘境
        </div>
      </div>

      <style>{`
        .ant-input {
          background: rgba(255,255,255,0.045) !important;
          border-color: rgba(220,235,255,0.10) !important;
          color: var(--text-primary) !important;
        }
        .ant-input::placeholder {
          color: rgba(220,235,255,0.38) !important;
        }
        .ant-input:hover {
          border-color: rgba(220,235,255,0.18) !important;
        }
        .ant-input:focus, .ant-input-focused {
          border-color: rgba(235,220,155,0.45) !important;
          box-shadow: 0 0 0 3px rgba(235,220,155,0.06) !important;
        }
        .ant-form-item > .ant-form-item-label > label {
          color: rgba(220,235,255,0.65) !important;
          font-size: 13px !important;
        }
        .ant-form-item-explain-error {
          color: rgba(255,140,120,0.85) !important;
          font-size: 12px !important;
        }
        @media (max-width: 768px) {
          .forgot-panel {
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 36px 24px !important;
          }
        }
        @media (max-width: 480px) {
          .forgot-panel {
            margin-left: 12px !important;
            margin-right: 12px !important;
            width: calc(100% - 24px) !important;
            max-width: 100% !important;
            padding: 28px 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
